import React, { ChangeEvent, useRef, useState, useEffect } from "react";
import { DeleteModal } from "../../components/Modal";
import { Heic, Pdf, UploadIcon } from "../../assets/svg";
import { BsTrash3 } from "react-icons/bs";
import { useFormContext } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

const accept =
  "image/jpeg, image/png, application/pdf, image/heic, .heic, .HEIC";
const fileSizeLimit = 10 * 1024 * 1024;
const fileAllowedExtensions = ["png", "jpg", "jpeg", "pdf", "heic"];

function MissingLineItem() {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const currentFile = useRef<File>(null);
  const { t } = useTranslation();

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const {
      target: { files: inputFiles },
    } = event;
    event.persist();
    if (!inputFiles) {
      return;
    }
    const fileArray = Array.from(inputFiles);

    for (const idx in fileArray) {
      const file = fileArray[idx];
      if (fileSizeLimit && file.size > fileSizeLimit) {
        toast.error(
          `File is too big. Should be less than ${
            fileSizeLimit / (1024 * 1024)
          } MB`,
          {
            position: "bottom-center",
          }
        );
        event.target.value = "";
        return;
      }
      const fileName = file.name;
      const fileExtension = fileName.substring(
        fileName.lastIndexOf(".") + 1,
        fileName?.length
      );
      if (!fileAllowedExtensions.includes(fileExtension.toLowerCase())) {
        toast.error(`File ${file.name} is not supported`, {
          position: "bottom-center",
        });
        event.target.value = "";
        return;
      }
    }

    if (event.target.files && event.target.files.length >= 1) {
      clearErrors("file");
    }
  };

  const {
    register,
    setValue,
    clearErrors,
    formState: { errors },
    watch,
  } = useFormContext();

  const watchedFile = watch("file");

  useEffect(() => {
    const files: File[] = Array.from(watchedFile || []);
    if (files.length > 0) {
      const file: File = files[0];
      const url = URL.createObjectURL(file);
      setImagePreview(url);
      currentFile.current = file;
    }
  }, [watchedFile]);

  return (
    <div>
      <div className="mt-2 mb-8">
        <h3 className="text-gray-500">
          {t(
            "We can only process claims with missing line items when you provide a copy of your receipt or invoice. Please upload your receipt or invoice before proceeding."
          )}
        </h3>
      </div>
      <DeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onYesClick={() => {
          setImagePreview(null);
          currentFile.current = null;
          setValue("file", null);
          setShowDeleteModal(false);
        }}
        onNoClick={() => setShowDeleteModal(false)}
        message={"Are you sure want to delete this image?"}
      />
      <div className="flex-col flex justify-center items-center">
        <div className="bg-[#2D6CCA14] border-[#B5C2D1] rounded border-2 relative w-fit border-dashed  transition-all py-5 px-[18px] ">
          {imagePreview ? (
            <div className="w-fit">
              {currentFile.current?.name?.toLowerCase().includes(".pdf") ? (
                <Pdf height="144" width="144" />
              ) : currentFile.current?.name?.toLowerCase().includes("heic") ? (
                <Heic height="144" width="144" />
              ) : (
                <img
                  src={imagePreview}
                  alt=""
                  key={"imagePreview"}
                  loading="eager"
                  className="max-h-96 object-contain"
                />
              )}
              <button
                className="absolute right-1 top-1 size-7 rounded shadow bg-red-600  hover:bg-red-700 flex justify-center items-center"
                type="button"
                onClick={() => setShowDeleteModal(true)}
              >
                <BsTrash3 className="text-white" />
              </button>
            </div>
          ) : (
            <div className="w-72 flex flex-col justify-center items-center max-w-72">
              <UploadIcon size={48} />
              <span className="text-[#2D6CCA] text-sm text-center ">
                {t("Upload your file..")}
              </span>
              <input
                type="file"
                title=" "
                accept={accept}
                multiple={false}
                className="absolute inset-0 text-transparent opacity-0 cursor-pointer"
                {...register("file", {
                  onChange: handleFileChange,
                  required:
                    "Receipt or invoice copy required.\nPlease upload one of these documents.",
                })}
              />
            </div>
          )}
        </div>
        {errors.file && (
          <span className="text-red-600 mt-1 whitespace-break-spaces select-none text-center w-[328px] text-sm">
            {t(errors?.file?.message as string)}
          </span>
        )}
      </div>
    </div>
  );
}

export default MissingLineItem;
